import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/lib/auth/AuthProvider";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import OnboardingGate from "@/components/auth/OnboardingGate";
import Onboarding from "./pages/Onboarding.tsx";
import Index from "./pages/Index.tsx";
import RootRoute from "@/components/routing/RootRoute";
import NotFound from "./pages/NotFound.tsx";
import Privacy from "./pages/legal/Privacy.tsx";
import Terms from "./pages/legal/Terms.tsx";
import Cookies from "./pages/legal/Cookies.tsx";
import AiPolicy from "./pages/legal/AiPolicy.tsx";
import Dmca from "./pages/legal/Dmca.tsx";
import Fryqnc from "./pages/apps/Fryqnc.tsx";
import Pulsynq from "./pages/apps/Pulsynq.tsx";
import MindLynq from "./pages/apps/MindLynq.tsx";
import Ablelynq from "./pages/apps/Ablelynq.tsx";
import Deafwav from "./pages/apps/Deafwav.tsx";
import Fullynq from "./pages/apps/Fullynq.tsx";
import Jadesynq from "./pages/apps/Jadesynq.tsx";
import Openlynq from "./pages/apps/Openlynq.tsx";
import Spiritsynq from "./pages/apps/Spiritsynq.tsx";
import Welcome from "./pages/apps/Welcome.tsx";
import Auth from "./pages/Auth.tsx";
import AuthCallback from "./pages/AuthCallback.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import Account from "./pages/Account.tsx";
import Pricing from "./pages/Pricing.tsx";
import AlaCarte from "./pages/AlaCarte.tsx";
import PublicProfile from "./pages/PublicProfile.tsx";
import Discover from "./pages/Discover.tsx";
import Matches from "./pages/Matches.tsx";
import Messages from "./pages/Messages.tsx";
import Conversation from "./pages/Conversation.tsx";
import Raela from "./pages/Raela.tsx";
import Safety from "./pages/Safety.tsx";
import Influencer from "./pages/Influencer.tsx";
import InfluencerAccess from "./pages/InfluencerAccess.tsx";
import PartnerAccess from "./pages/PartnerAccess.tsx";
import SandboxWelcome from "./pages/SandboxWelcome.tsx";
import StorePage from "./pages/Store.tsx";
import StoreCategoryPage from "./pages/store/StoreCategoryPage.tsx";
import StoreVendorPage from "./pages/store/StoreVendorPage.tsx";
import StoreProductPage from "./pages/store/StoreProductPage.tsx";
import StoreBookDetailPage from "./pages/store/StoreBookDetailPage.tsx";
import StoreCartPage from "./pages/store/StoreCartPage.tsx";
import StoreSuccessPage from "./pages/store/StoreSuccessPage.tsx";
import StoreOrdersPage from "./pages/store/StoreOrdersPage.tsx";
import StoreOrderDetailPage from "./pages/store/StoreOrderDetailPage.tsx";
import StoreVendorSetup from "./pages/store/StoreVendorSetup.tsx";
import VendorPortalLayout from "./components/store/VendorPortalLayout";
import VendorDashboard from "./pages/store/vendor/VendorDashboard";
import VendorProducts from "./pages/store/vendor/VendorProducts";
import VendorProductEditor from "./pages/store/vendor/VendorProductEditor";
import VendorOrders from "./pages/store/vendor/VendorOrders";
import VendorOrderDetail from "./pages/store/vendor/VendorOrderDetail";
import VendorRefunds from "./pages/store/vendor/VendorRefunds";
import VendorPayouts from "./pages/store/vendor/VendorPayouts";
import VendorSettings from "./pages/store/vendor/VendorSettings";
import Founder from "./pages/Founder.tsx";
import Mentors from "./pages/Mentors.tsx";
import MentorApply from "./pages/MentorApply.tsx";
import MentorSessions from "./pages/MentorSessions.tsx";
import Events from "./pages/Events.tsx";
import NewEvent from "./pages/NewEvent.tsx";
import EventDetail from "./pages/EventDetail.tsx";
import EventCheckIn from "./pages/EventCheckIn.tsx";
import Inbox from "./pages/Inbox.tsx";
import Contribute from "./pages/Contribute.tsx";
import Support from "./pages/Support.tsx";
import EmergencyButton from "./components/safety/EmergencyButton";
import CrisisResourcesTab from "./components/safety/CrisisResourcesTab";
import MobileBottomNav from "./components/MobileBottomNav";
import { BroadcastBanner } from "./components/broadcasts/BroadcastBanner";
import { SafetyAlertsProvider } from "@/lib/safety/useSafetyAlerts";
import BrandHead from "@/components/BrandHead";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <BrandHead />
        <AuthProvider>
          <SafetyAlertsProvider>
            <Routes>
              <Route path="/" element={<RootRoute />} />
              <Route path="/fryqnc" element={<Fryqnc />} />
              <Route path="/pulsynq" element={<Pulsynq />} />
              <Route path="/mindlynq" element={<MindLynq />} />
              <Route path="/ablelynq" element={<Ablelynq />} />
              <Route path="/deafwav" element={<Deafwav />} />
              <Route path="/fullynq" element={<Fullynq />} />
              <Route path="/jadesynq" element={<Jadesynq />} />
              <Route path="/openlynq" element={<Openlynq />} />
              <Route path="/spiritsynq" element={<Spiritsynq />} />
              <Route path="/welcome/:appId" element={<Welcome />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/a-la-carte" element={<AlaCarte />} />
              <Route path="/u/:profileId" element={<PublicProfile />} />
              <Route path="/discover" element={<Discover />} />
              <Route path="/matches" element={<Matches />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/messages/:conversationId" element={<Conversation />} />
              <Route path="/raela" element={<Raela />} />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <OnboardingGate>
                    <Account />
                  </OnboardingGate>
                }
              />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="/ai-policy" element={<AiPolicy />} />
              <Route path="/dmca" element={<Dmca />} />
              <Route path="/safety" element={<Safety />} />
              <Route path="/influencer" element={<Influencer />} />
              <Route path="/influencer-access" element={<InfluencerAccess />} />
              <Route path="/partner-access" element={<PartnerAccess />} />
              <Route path="/sandbox-welcome" element={<SandboxWelcome />} />
              <Route path="/store" element={<StorePage />} />
              <Route path="/store/category/:slug" element={<StoreCategoryPage />} />
              <Route path="/store/vendor/:slug" element={<StoreVendorPage />} />
              <Route path="/store/product/:slug" element={<StoreProductPage />} />
              <Route path="/store/book/:slug" element={<StoreBookDetailPage />} />
              <Route path="/store/cart" element={<StoreCartPage />} />
              <Route path="/store/checkout/success" element={<StoreSuccessPage />} />
              <Route path="/store/orders" element={<StoreOrdersPage />} />
              <Route path="/store/orders/:id" element={<StoreOrderDetailPage />} />
              <Route path="/store/vendor-setup" element={<StoreVendorSetup />} />
              <Route path="/store/vendor" element={<VendorPortalLayout />}>
                <Route index element={<VendorDashboard />} />
                <Route path="products" element={<VendorProducts />} />
                <Route path="products/new" element={<VendorProductEditor />} />
                <Route path="products/:id" element={<VendorProductEditor />} />
                <Route path="orders" element={<VendorOrders />} />
                <Route path="orders/:id" element={<VendorOrderDetail />} />
                <Route path="refunds" element={<VendorRefunds />} />
                <Route path="payouts" element={<VendorPayouts />} />
                <Route path="settings" element={<VendorSettings />} />
              </Route>
              <Route path="/founder" element={<Founder />} />
              <Route path="/mentors" element={<Mentors />} />
              <Route path="/mentor-apply" element={<MentorApply />} />
              <Route path="/mentor-sessions" element={<MentorSessions />} />
              <Route path="/events" element={<Events />} />
              <Route path="/events/new" element={<NewEvent />} />
              <Route path="/events/:eventId" element={<EventDetail />} />
              <Route path="/events/:eventId/check-in" element={<EventCheckIn />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/contribute" element={<Contribute />} />
              <Route path="/support" element={<Support />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <BroadcastBanner />
            <EmergencyButton />
            <CrisisResourcesTab />
            <MobileBottomNav />
          </SafetyAlertsProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
